import {
  type AssetsApprovalSchema,
  type AssetsBulkShareCreateSchema,
  type AssetsHttpGetByObjectTypeByObjectIdSharesByShareIdUsersQuery,
  type AssetsHttpPutByObjectTypeByObjectIdSharesByShareIdQuery,
  type AssetsPlaylistCreateSchema,
  type AssetsShareCreateSchema,
  type AssetsShareSchema,
  type AssetsShareUserSchemaPartial,
  httpDeleteByObjectTypeByObjectIdSharesByShareId,
  httpGetAssetsByAssetIdSharesAll,
  httpGetByObjectTypeByObjectIdShares,
  httpGetByObjectTypeByObjectIdSharesByShareIdUsers,
  httpGetCollectionsByCollectionIdSharesAll,
  httpPatchByObjectTypeByObjectIdSharesByShareIdUsersByShareUserId,
  httpPostByObjectTypeByObjectIdApprovalsRequest,
  httpPostByObjectTypeByObjectIdShares,
  httpPostByObjectTypeByObjectIdSharesUrl,
  httpPostPlaylists,
  httpPostShareByObjectType,
  httpPutByObjectTypeByObjectIdSharesByShareId,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  OBJECT_TYPE_PLAYLISTS,
  type ObjectType,
} from '@iconik-io/iconik-api/src/types/object_types';
import { deleteNormalizedEntity } from '@iconik-io/shared-lib/src/redux/entities/slice';

import type { AppDispatch } from '../../store/types';
import {
  type CommonListGetParams,
  wrapCommonCreate,
  wrapCommonDelete,
  wrapCommonGetList,
} from '../common_list/thunks';
import { successMessage } from '../messages/thunks';

type ListGetParams = CommonListGetParams & { last_id?: string };

function getShares(
  objectType: ObjectType,
  objectId: string,
  params: ListGetParams = {},
) {
  return (dispatch: AppDispatch) =>
    wrapCommonGetList({
      prefix: 'panel_shares',
      dispatch,
      params,
      getListPromise: () =>
        dispatch(
          httpGetByObjectTypeByObjectIdShares(objectType, objectId, params),
        ),
    });
}

export function getAssetSharesAll(assetId: string) {
  return (dispatch: AppDispatch) =>
    wrapCommonGetList({
      prefix: 'panel_shares',
      dispatch,
      params: {},
      getListPromise: () => dispatch(httpGetAssetsByAssetIdSharesAll(assetId)),
    });
}

export function getCollectionSharesAll(collectionId: string) {
  return (dispatch: AppDispatch) =>
    wrapCommonGetList({
      prefix: 'panel_shares',
      dispatch,
      params: {},
      getListPromise: () =>
        dispatch(httpGetCollectionsByCollectionIdSharesAll(collectionId)),
    });
}

export function getSharesRecursive(
  objectType: ObjectType,
  id: string,
  data: ListGetParams = {},
) {
  return (dispatch: AppDispatch) =>
    dispatch(getShares(objectType, id, data)).then((shares) => {
      if (shares.body.objects.length === 50) {
        const params = {
          last_id: shares.body.objects[shares.body.objects.length - 1].id,
        };
        dispatch(getShares(objectType, id, params));
      }
    });
}

export function createShare(
  objectType: ObjectType,
  objectId: string,
  data: AssetsShareCreateSchema,
) {
  return (dispatch: AppDispatch) =>
    wrapCommonCreate({
      prefix: 'panel_shares',
      dispatch,
      createPromise: dispatch(
        httpPostByObjectTypeByObjectIdShares(objectType, objectId, data),
      ),
    });
}

export function createShareUrl(
  objectType: ObjectType,
  objectId: string,
  data: AssetsShareCreateSchema,
) {
  return (dispatch: AppDispatch) =>
    wrapCommonCreate({
      prefix: 'panel_shares',
      dispatch,
      createPromise: dispatch(
        httpPostByObjectTypeByObjectIdSharesUrl(objectType, objectId, data),
      ),
    });
}

export function deleteSharesByIds(
  objectType: ObjectType,
  objectId: string,
  ids: string[],
) {
  return (dispatch: AppDispatch): Promise<void> =>
    wrapCommonDelete({
      prefix: 'panel_shares',
      dispatch,
      ids,
      deletePromise: Promise.all(
        ids.map((id) =>
          dispatch(
            httpDeleteByObjectTypeByObjectIdSharesByShareId(
              objectType,
              objectId,
              id,
            ),
          ).then(() => {
            dispatch(deleteNormalizedEntity({ key: 'shares', id }));
          }),
        ),
      ),
    });
}

export function updateSharesByShareId(
  objectType: ObjectType,
  objectId: string,
  id: string,
  shareData: AssetsShareSchema,
) {
  return (dispatch: AppDispatch) =>
    dispatch(
      httpPutByObjectTypeByObjectIdSharesByShareId(
        objectType,
        objectId,
        id,
        shareData,
        { index_immediately: true },
      ),
    ).then((updatedShare) => {
      dispatch(successMessage({ text: 'Share Updated' }));
      return updatedShare;
    });
}

export function createBulkShare(
  objectType: string,
  body: AssetsBulkShareCreateSchema,
) {
  return (dispatch: AppDispatch) =>
    dispatch(httpPostShareByObjectType(objectType, body));
}

export function updateShare(
  objectType: string,
  objectId: string,
  shareId: string,
  body: AssetsShareSchema,
  query?: AssetsHttpPutByObjectTypeByObjectIdSharesByShareIdQuery,
) {
  return (dispatch: AppDispatch) =>
    dispatch(
      httpPutByObjectTypeByObjectIdSharesByShareId(
        objectType,
        objectId,
        shareId,
        body,
        query,
      ),
    );
}

export function createPlaylist(playlistData: AssetsPlaylistCreateSchema) {
  return (dispatch: AppDispatch) =>
    dispatch(httpPostPlaylists(playlistData, { index_immediately: true }));
}

export function createPlaylistApprovalBulkRequest(
  playlistId: string,
  approvalData: AssetsApprovalSchema,
) {
  // This API will iterate over all assets in a playlist and create an approval request
  return (dispatch: AppDispatch) =>
    dispatch(
      httpPostByObjectTypeByObjectIdApprovalsRequest(
        OBJECT_TYPE_PLAYLISTS,
        playlistId,
        approvalData,
      ),
    );
}

export function getShareUsers(
  objectType: string,
  objectId: string,
  shareId: string,
  query?: AssetsHttpGetByObjectTypeByObjectIdSharesByShareIdUsersQuery,
) {
  return (dispatch: AppDispatch) =>
    dispatch(
      httpGetByObjectTypeByObjectIdSharesByShareIdUsers(
        objectType,
        objectId,
        shareId,
        query,
      ),
    );
}

export function updateShareUser(
  objectType: string,
  objectId: string,
  shareId: string,
  shareUserId: string,
  body: AssetsShareUserSchemaPartial,
) {
  return (dispatch: AppDispatch) =>
    dispatch(
      httpPatchByObjectTypeByObjectIdSharesByShareIdUsersByShareUserId(
        objectType,
        objectId,
        shareId,
        shareUserId,
        body,
      ),
    );
}
